import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { kioskInstanceApi, shopApi, recipeApi } from '@xoxokiosk/shared';
import type { KioskInstanceResponse as KioskInstance, ShopResponse as SharedShop, Recipe as SharedRecipe } from '@xoxokiosk/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Alert, AlertDescription } from '@xoxokiosk/ui';
import { ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';

// Define the Shop interface with all required properties
interface Shop {
  shop_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  recipes?: string[]; // Array of recipe IDs
  settings?: Record<string, unknown>;
  shop?: Shop; // Nested shop property
  order_default_size?: 'sizeS' | 'sizeM' | 'sizeL';
  order_default_quantity?: number;
  order_default_status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  userId?: string;
}

type Recipe = SharedRecipe;

// Define common props for all admin components
interface AdminComponentProps {
  instance: KioskInstance;
  shop: Shop;
  recipes: Recipe[];
  onSave?: () => void;
  onError?: (error: string) => void;
}

// Lazy load admin components with proper typing and error handling
const ToyKioskAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  import('@xoxokiosk/toy-kiosk')
    .then(module => {
      // Check if ToyKioskAdminPage exists in the module
      if (module.ToyKioskAdminPage) {
        return { default: module.ToyKioskAdminPage };
      }
      console.warn('ToyKioskAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => <div>Toy Kiosk Admin (Default)</div> };
    })
    .catch(error => {
      console.error('Failed to load ToyKioskAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Toy Kiosk Admin</div> };
    })
);

// Lazy load FiveMuiAdmin component
const FiveMuiAdmin = lazy<React.ComponentType<AdminComponentProps>>(() =>
  import('@xoxokiosk/five-mui')
    .then((module: any) => {
      if (module.FiveMuiAdminPage) {
        return { default: module.FiveMuiAdminPage as React.ComponentType<AdminComponentProps> };
      }
      console.warn('FiveMuiAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => (
        <div className="p-6 text-center">
          <h2>Five-Mui Admin</h2>
          <p>The admin page for five-mui is not yet implemented.</p>
          <p>Instance ID: {props.instance.join_code}</p>
          <p>Shop: {props.shop.name}</p>
        </div>
      ) };
    })
    .catch(error => {
      console.error('Failed to load FiveMuiAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Five-Mui Admin</div> };
    })
);

// Lazy load StoryTreeAdmin component
const StoryTreeAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  // Use relative path to avoid module resolution issues
  import('@xoxokiosk/story-tree')
    .then(module => {
      if (module.StoryTreeAdminPage) {
        return { default: module.StoryTreeAdminPage };
      }
      console.warn('StoryTreeAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => <div>Story Tree Admin (Default)</div> };
    })
    .catch(error => {
      console.error('Failed to load StoryTreeAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Story Tree Admin</div> };
    })
);

// Lazy load MBTIAdmin component
const MBTIAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  // Use relative path to avoid module resolution issues
  import('@xoxokiosk/mbti')
    .then(module => {
      if (module.MBTIAdminPage) {
        return { default: module.MBTIAdminPage };
      }
      console.warn('MBTIAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => <div>MBTI Style Transfer Admin (Default)</div> };
    })
    .catch(error => {
      console.error('Failed to load MBTIAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load MBTI Style Transfer Admin</div> };
    })
);

// Lazy load OrderingTerminalAdmin component
const OrderingTerminalAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  // Use relative path to avoid module resolution issues
  import('@xoxokiosk/ordering-terminal')
    .then(module => {
      // Always use fallback since OrderingTerminalAdminPage likely doesn't exist yet
      console.warn('Using fallback for OrderingTerminalAdminPage');
      return { 
        default: (props: AdminComponentProps) => (
          <div className="p-6 text-center">
            <h2>Ordering Terminal Admin</h2>
            <p>The admin page for ordering-terminal experience is not yet implemented.</p>
            <p>Instance ID: {props.instance.join_code}</p>
            <p>Shop: {props.shop.name}</p>
          </div>
        ) 
      };
    })
    .catch(error => {
      console.error('Failed to load OrderingTerminalAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Ordering Terminal Admin</div> };
    })
);

// Lazy load EzPairingAdmin component
const EzPairingAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  import('@xoxokiosk/ez-pairing')
    .then(module => {
      if (module.EzPairingAdminPage) {
        return { default: module.EzPairingAdminPage };
      }
      console.warn('EzPairingAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => <div>EZ Pairing Admin (Default)</div> };
    })
    .catch(error => {
      console.error('Failed to load EzPairingAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load EZ Pairing Admin</div> };
    })
);

// Lazy load VibesMatchingAdmin component
const VibesMatchingAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  import('@xoxokiosk/vibes-matching')
    .then(module => {
      if (module.VibesMatchingAdminPage) {
        return { default: module.VibesMatchingAdminPage };
      }
      console.warn('VibesMatchingAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => <div>Vibes Matching Admin (Default)</div> };
    })
    .catch(error => {
      console.error('Failed to load VibesMatchingAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Vibes Matching Admin</div> };
    })
);

// Lazy load VoiceSommelierAdmin component
const VoiceSommelierAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  import('@xoxokiosk/voice-sommelier')
    .then((module: any) => {
      if (module.VoiceSommelierAdminPage) {
        return { default: module.VoiceSommelierAdminPage as React.ComponentType<AdminComponentProps> };
      }
      console.warn('VoiceSommelierAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => (
        <div className="p-6 text-center">
          <h2>Voice Sommelier Admin</h2>
          <p>The admin page for voice-sommelier is not yet implemented.</p>
          <p>Instance ID: {props.instance.join_code}</p>
          <p>Shop: {props.shop.name}</p>
        </div>
      ) };
    })
    .catch(error => {
      console.error('Failed to load VoiceSommelierAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Voice Sommelier Admin</div> };
    })
);

// Lazy load DOTDAdmin component
const DOTDAdmin = lazy<React.ComponentType<AdminComponentProps>>(() => 
  import('@xoxokiosk/dotd')
    .then((module: any) => {
      if (module.DOTDAdminPage) {
        return { default: module.DOTDAdminPage as React.ComponentType<AdminComponentProps> };
      }
      console.warn('DOTDAdminPage not found in module, using fallback');
      return { default: (props: AdminComponentProps) => (
        <div className="p-6 text-center">
          <h2>Drink of the Day Admin</h2>
          <p>The admin page for DOTD is not yet implemented.</p>
          <p>Instance ID: {props.instance.join_code}</p>
          <p>Shop: {props.shop.name}</p>
        </div>
      ) };
    })
    .catch(error => {
      console.error('Failed to load DOTDAdmin:', error);
      return { default: (props: AdminComponentProps) => <div>Failed to load Drink of the Day Admin</div> };
    })
);

// Lazy load MirakoAvatarAdmin component
const MirakoAvatarAdmin = lazy<React.ComponentType<AdminComponentProps>>(() =>
  import('@xoxokiosk/mirako-avatar')
    .then((module: any) => {
      if (module.MirakoAvatarAdminPage) {
        return { default: module.MirakoAvatarAdminPage as React.ComponentType<AdminComponentProps> };
      }
      console.warn('MirakoAvatarAdminPage not found in module, using fallback');
      return { default: (_props: AdminComponentProps) => <div>Mirako Avatar Admin (Default)</div> };
    })
    .catch(error => {
      console.error('Failed to load MirakoAvatarAdmin:', error);
      return { default: (_props: AdminComponentProps) => <div>Failed to load Mirako Avatar Admin</div> };
    })
);

const InstanceAdminPage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [instance, setInstance] = useState<KioskInstance | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!instanceId) {
        setError('No instance ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch instance data
        const { data: instanceData } = await kioskInstanceApi.getKioskInstanceByCode(instanceId);
        setInstance(instanceData);
        
        if (!instanceData) {
          setError('Instance not found');
          setLoading(false);
          return;
        }
        
        // Fetch shop data
        if (instanceData.shop_id) {
          const { data: shopData } = await shopApi.getShopById(instanceData.shop_id);
          setShop(shopData as Shop);
          
          // Fetch recipes for the shop
          try {
            // Use getAdminRecipes as fallback if getRecipes is not available
            const { data: recipesData } = await recipeApi.getAdminRecipes(instanceData.shop_id);
            setRecipes(recipesData.recipes || []);
          } catch (recipeErr) {
            console.error('Failed to load recipes:', recipeErr);
            setRecipes([]);
          }
        } else {
          setError('No shop associated with this instance');
        }
      } catch (err) {
        console.error('Error loading instance data:', err);
        setError('Failed to load instance data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [instanceId]);


  const handleSave = () => {
    setSaveStatus('Configuration saved successfully!');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleError = (errorMsg: string) => {
    setError(errorMsg);
    setTimeout(() => setError(''), 5000);
  };

  const renderAdminComponent = () => {
    if (!instance || !shop) return null;
    
    // Determine which admin component to render based on the experience type
    // Use kiosk_type_identifier as the experience type
    const experienceType = (instance.kiosk_type_identifier || '').toLowerCase();
    
    try {
      switch (experienceType) {
        case 'mirako-avatar':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading mirako-avatar admin...</div>}>
              <MirakoAvatarAdmin
                instance={instance}
                shop={shop}
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );
        case 'toy-kiosk':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading toy-kiosk admin...</div>}>
              <ToyKioskAdmin 
                instance={instance} 
                shop={shop} 
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );
          
        case 'story-tree':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading story-tree admin...</div>}>
              <StoryTreeAdmin 
                instance={instance} 
                shop={shop} 
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );

        case 'mbti':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading MBTI Style Transfer admin...</div>}>
              <MBTIAdmin 
                instance={instance} 
                shop={shop} 
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );
          
        case 'ordering-terminal':
          try {
            return (
              <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading ordering-terminal admin...</div>}>
                <OrderingTerminalAdmin 
                  instance={instance} 
                  shop={shop} 
                  recipes={recipes}
                  onSave={handleSave}
                  onError={handleError}
                />
              </Suspense>
            );
          } catch (err) {
            return renderNoAdminPage(experienceType);
          }
        case 'ez-pairing':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading ez-pairing admin...</div>}>
              <EzPairingAdmin 
                instance={instance} 
                shop={shop} 
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );

        case 'vibes-matching':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading vibes-matching admin...</div>}>
              <VibesMatchingAdmin 
                instance={instance} 
                shop={shop} 
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );

        case 'voice-sommelier':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading voice-sommelier admin...</div>}>
              <VoiceSommelierAdmin
                instance={instance}
                shop={shop}
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );
        case 'five-mui':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading five-mui admin...</div>}>
              <FiveMuiAdmin
                instance={instance}
                shop={shop}
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );

        case 'dotd':
          return (
            <Suspense fallback={<div className="flex items-center justify-center p-8 text-muted-foreground">Loading Drink of the Day admin...</div>}>
              <DOTDAdmin
                instance={instance}
                shop={shop}
                recipes={recipes}
                onSave={handleSave}
                onError={handleError}
              />
            </Suspense>
          );
          
        default:
          return renderNoAdminPage(experienceType);
      }
    } catch (error) {
      console.error(`Error rendering admin for ${experienceType}:`, error);
      return renderNoAdminPage(experienceType);
    }
  };
  
  const renderNoAdminPage = (experienceType: string) => {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Admin Configuration Available</CardTitle>
          <CardDescription>
            There is no admin configuration page available for the "{experienceType}" experience type.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/shop-admin')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold tracking-tight">
                Instance Admin: {instance?.join_code || 'Loading...'}
              </h1>
              {instance && shop && (
                <p className="text-muted-foreground mt-1">
                  {shop.name} • {instance.kiosk_type_identifier}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {saveStatus && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{saveStatus}</AlertDescription>
          </Alert>
        )}

        {/* Content */}
        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading instance data...</span>
              </div>
            </CardContent>
          </Card>
        ) : instance ? (
          <React.Suspense fallback={
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading admin component...</span>
                </div>
              </CardContent>
            </Card>
          }>
            {renderAdminComponent()}
          </React.Suspense>
        ) : (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Instance not found or could not be loaded</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default InstanceAdminPage;
